import { Alert, Center, CloseButton, Combobox, Loader, rem, Stack } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import { BaseFieldSelectStore } from 'stores/local/shared/form/FieldSelectStore';

type OptionsDropdownProps = {
  className?: string;
  store: BaseFieldSelectStore;
  optionsFooter?: React.ReactNode;
};

const OptionsDropdown: React.FC<OptionsDropdownProps> = ({ className, store, optionsFooter = null }) => {
  const { ref, inView } = useInView();

  React.useEffect(() => {
    if (inView) {
      if (store.options.length === 0 || store.canLoadMore) {
        store.load();
      }
    }
  }, [store, inView]);

  const onSearchChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      store.setSearchValue(event.currentTarget.value);
    },
    [store],
  );

  return (
    <Combobox.Dropdown className={className}>
      <Combobox.Search
        value={store.searchValue}
        onChange={onSearchChange}
        placeholder="Поиск"
        rightSectionPointerEvents={store.searchValue.length > 0 ? 'auto' : undefined}
        rightSection={store.searchValue.length > 0 && <CloseButton size="sm" onClick={store.resetSearch} />}
      />
      <Combobox.Options style={{ overflowY: 'auto', maxHeight: rem(200) }}>
        {store.meta.isError && store.options.length === 0 && (
          <Alert variant="light" color="red">
            Произошла ошибка, попробуйте повторить позже
          </Alert>
        )}
        {store.meta.isSuccess && store.options.length === 0 && (
          <Combobox.Empty c="dimmed">Данные не найдены</Combobox.Empty>
        )}

        <Stack gap={rem(2)}>
          {store.options.map((item) => {
            return (
              <Combobox.Option selected={store.valueKeysSet.has(item.key)} value={item.key} key={item.key}>
                {item.value}
              </Combobox.Option>
            );
          })}
        </Stack>

        <div ref={ref} style={{ height: '1px' }} />

        {(store.meta.isLoading || store.meta.isNotStarted) && (
          <Center mih={rem(50)}>
            <Loader size="xs" />
          </Center>
        )}
      </Combobox.Options>
      {optionsFooter}
    </Combobox.Dropdown>
  );
};

export default observer(OptionsDropdown);
